import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Save, User, Palette, Image, Trash2, Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const PRESET_COLORS = [
  { name: 'Púrpura', value: '#9333ea' },
  { name: 'Rosa', value: '#ec4899' },
  { name: 'Rojo', value: '#ef4444' },
  { name: 'Naranja', value: '#f97316' },
  { name: 'Amarillo', value: '#eab308' },
  { name: 'Verde', value: '#22c55e' },
  { name: 'Azul', value: '#3b82f6' },
  { name: 'Celeste', value: '#06b6d4' },
  { name: 'Índigo', value: '#6366f1' },
  { name: 'Negro', value: '#1f2937' },
];

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    primary_color: '#9333ea',
    background_image: null
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const data = response.data;
      setAdminData(data);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        primary_color: data.primary_color || '#9333ea',
        background_image: data.background_image || null
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, primary_color: color }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (máx 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no puede superar 2MB');
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData(prev => ({ ...prev, background_image: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, background_image: '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      
      if (formData.name !== adminData.name) {
        updateData.name = formData.name;
      }
      if (formData.email !== adminData.email) {
        updateData.email = formData.email;
      }
      if (formData.primary_color !== (adminData.primary_color || '#9333ea')) {
        updateData.primary_color = formData.primary_color;
      }
      if (formData.background_image !== adminData.background_image) {
        updateData.background_image = formData.background_image || '';
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('No hay cambios para guardar');
        setSaving(false);
        return;
      }

      await axios.put(`${API}/auth/profile`, updateData);
      toast.success('Configuración guardada correctamente');
      
      // Actualizar datos locales
      setAdminData(prev => ({ ...prev, ...updateData }));
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validaciones
    if (!passwordData.current_password) {
      toast.error('Ingresá tu contraseña actual');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Ingresá la nueva contraseña');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('¡Contraseña actualizada correctamente!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h2 className="text-3xl font-bold">Mi Perfil</h2>
        <p className="text-gray-600">Configurá tus datos y personalizá tu página de rifas</p>
      </div>

      {/* Datos Personales */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User size={20} />
            Datos Personales
          </CardTitle>
          <CardDescription>Tu información básica de cuenta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Tu nombre"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="tu@email.com"
            />
          </div>
        </CardContent>
      </Card>

      {/* Personalización */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette size={20} />
            Personalización
          </CardTitle>
          <CardDescription>Personalizá cómo ven tus rifas los clientes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selector de Color */}
          <div className="space-y-3">
            <Label>Color Principal</Label>
            <div className="flex flex-wrap gap-3">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => handleColorSelect(color.value)}
                  className={`w-10 h-10 rounded-full transition-all ${
                    formData.primary_color === color.value
                      ? 'ring-4 ring-offset-2 ring-gray-400 scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <Label htmlFor="custom-color" className="text-sm text-gray-600">
                O elegí un color personalizado:
              </Label>
              <input
                id="custom-color"
                type="color"
                value={formData.primary_color}
                onChange={(e) => handleColorSelect(e.target.value)}
                className="w-10 h-10 cursor-pointer rounded"
              />
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {formData.primary_color}
              </span>
            </div>
          </div>

          {/* Vista previa del color */}
          <div className="space-y-2">
            <Label>Vista previa</Label>
            <div
              className="h-20 rounded-lg flex items-center justify-center text-white font-bold text-lg"
              style={{
                background: `linear-gradient(135deg, ${formData.primary_color} 0%, ${adjustColor(formData.primary_color, -30)} 100%)`
              }}
            >
              Así verán tus clientes el fondo
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Imagen de Fondo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image size={20} />
            Imagen de Fondo
          </CardTitle>
          <CardDescription>Opcionalmente, subí una imagen para tu página</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.background_image ? (
            <div className="space-y-3">
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={formData.background_image}
                  alt="Fondo"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">Vista previa</span>
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRemoveImage}
              >
                <Trash2 size={16} className="mr-2" />
                Quitar imagen
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Image size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">
                Subí una imagen de fondo (máx. 2MB)
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button variant="outline" asChild>
                  <span>Seleccionar imagen</span>
                </Button>
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cambiar Contraseña */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock size={20} />
            Cambiar Contraseña
          </CardTitle>
          <CardDescription>Actualizá tu contraseña de acceso</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Contraseña actual</Label>
            <div className="relative">
              <Input
                id="current_password"
                type={showPasswords.current ? "text" : "password"}
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Tu contraseña actual"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPasswords.new ? "text" : "password"}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirmar nueva contraseña</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Repetí la nueva contraseña"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={savingPassword}
            variant="outline"
            className="w-full"
          >
            {savingPassword ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Cambiando...
              </>
            ) : (
              <>
                <Lock className="mr-2" size={18} />
                Cambiar Contraseña
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Botón Guardar Perfil */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700"
          size="lg"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={20} />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={20} />
              Guardar Cambios del Perfil
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Función auxiliar para ajustar brillo del color
function adjustColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(Math.min((num >> 16) + amt, 255), 0);
  const G = Math.max(Math.min((num >> 8 & 0x00FF) + amt, 255), 0);
  const B = Math.max(Math.min((num & 0x0000FF) + amt, 255), 0);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

export default AdminSettings;
